import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, GripVertical, Trash2, AlertCircle } from 'lucide-react';
import { Controller } from 'react-hook-form';
import InfiniteSelect from '../../../../../components/InfiniteSelect/InfiniteSelect';
import ResourceServiceSelect from '../../../../../components/ResourceServiceSelect';
import Tooltip from '../../../../../components/Tooltip';
import {
  CollapsibleHeader,
  ExpandedItemContainer,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  StyledSelect
} from '../style';
import { fetchCompaniesByStage, fetchScriptsByProductId } from '../api';
import { useController } from 'react-hook-form';
import {
  FlexRow,
  FlexFormGroup,
  SortableItemContainer,
  ItemHeader,
  DragHandle,
  ItemTitle,
  HeaderControls,
  MoveButton,
  RemoveButton,
  ScriptSelectContainer,
  CollapsedItem,
  CollapsedItemContent,
  CollapsedItemControls,
  CircularCollapseIcon
} from './SortableItem.style';
import { TextArea } from "../../../../../components/ProductionFlow/GenerateRNCModal/style"

const SortableItemComponent: React.FC<any> = ({
  item,
  scriptIndex,
  index,
  totalItems,
  onRemove,
  onToggleExpansion,
  onMoveItem,
  expandedItems,
  getItemTypeLabel,
  control,
  updateItemWeight,
  updateItemIdealMeasurements,
  updateItemProductScript,
  updateItemReadyToExecute,
  updateItemUnitOfMeasurement,
  setValue,
  fetchRawMaterials,
  fetchResources,
  fetchProducts,
  fetchServices,
  handleItemTypeChange,
  validateProductFinalSelection,
  watch,
  errors,
  getValues,
}) => {
  const isExpanded = expandedItems.has(item.id);

  const formatScriptItemsForDisplay = (scriptItems: any[]) => {
    if (!scriptItems || scriptItems.length === 0) return 'Sem itens';
    return scriptItems.map(item => item.name).join(' → ');
  };

  const [previousServiceId, setPreviousServiceId] = useState<number | null>(null);

  const selectedResource = watch(`scripts.${scriptIndex}.items.${index}.resource`);
  const selectedService = watch(`scripts.${scriptIndex}.items.${index}.service`);

  const { field: unitField } = useController({
    name: `scripts.${scriptIndex}.items.${index}.unitOfMeasurementId`,
    control,
  });

  const selectedUnitId = watch(`scripts.${scriptIndex}.items.${index}.unitOfMeasurementId`);

  const getQuantityLabel = () => {
    if (item.type === 'resource' && selectedResource?.prices && selectedUnitId) {
      const selectedPrice = selectedResource.prices.find((price: any) => price.unitOfMeasurement?.id === selectedUnitId);
      if (selectedPrice?.unitOfMeasurement?.name) {
        return `Quantidade (${selectedPrice.unitOfMeasurement.name})`;
      }
    }
    return item.type === 'resource' ? 'Quantidade' : 'Peso';
  };

  const getQuantityPlaceholder = () => {
    if (item.type === 'resource' && selectedResource?.prices && selectedUnitId) {
      const selectedPrice = selectedResource.prices.find((price: any) => price.unitOfMeasurement?.id === selectedUnitId);
      if (selectedPrice?.unitOfMeasurement?.name) {
        const unit = selectedPrice.unitOfMeasurement.name.toLowerCase();
        return `Ex: 98,7${unit}`;
      }
    }
    return '98,7kg';
  };

  const getExecutionTimeLabel = () => {
    return 'Tempo de execução';
  };

  const shouldShowResourceUnitSelect = () => {
    return selectedResource?.prices && selectedResource.prices.length > 1;
  };

  const handleUnitSelect = (unitId: number) => {
    if (unitField) {
      unitField.onChange(unitId);
    }
  };

  const getAvailableUnits = () => {
    if (!selectedResource?.prices) {
      return [];
    }
    const units = selectedResource.prices.map((price: any) => ({
      id: price.unitOfMeasurement?.id || 0,
      name: price.unitOfMeasurement?.name || 'Unidade',
      price: price.price
    }));
    return units;
  };

  const shouldShowServiceUnitSelect = () => {
    return selectedService?.prices && selectedService.prices.length > 1;
  };

  const getAvailableServiceUnits = () => {
    if (!selectedService?.prices) {
      return [];
    }
    const units = selectedService.prices.map((price: any) => ({
      id: price.unitOfMeasurement?.id || 0,
      name: price.unitOfMeasurement?.name || 'Unidade',
      price: price.price
    }));
    return units;
  };

  const autoFilledRef = React.useRef(false);

  useEffect(() => {
    const currentCompany = watch(`scripts.${scriptIndex}.items.${index}.company`);

    if (!selectedService?.id) {
      if (currentCompany?.id) {
        setValue(`scripts.${scriptIndex}.items.${index}.company`, null);
      }
      autoFilledRef.current = false;
      return;
    }

    if (previousServiceId !== null && selectedService.id !== previousServiceId) {
      setValue(`scripts.${scriptIndex}.items.${index}.company`, null);
      setValue(`scripts.${scriptIndex}.items.${index}.description`, selectedService.description ?? null);
      setValue(`scripts.${scriptIndex}.items.${index}.observation`, selectedService.observation ?? null);
      autoFilledRef.current = true;
    }

    if (previousServiceId === null && !autoFilledRef.current) {
      const currentDescription = watch(`scripts.${scriptIndex}.items.${index}.description`);
      const currentObservation = watch(`scripts.${scriptIndex}.items.${index}.observation`);

      if (!currentDescription) {
        setValue(`scripts.${scriptIndex}.items.${index}.description`, selectedService.description ?? null);
      }
      if (!currentObservation) {
        setValue(`scripts.${scriptIndex}.items.${index}.observation`, selectedService.observation ?? null);
      }
      autoFilledRef.current = true;
    }

    setPreviousServiceId(selectedService?.id || null);
  }, [selectedService, scriptIndex, index, setValue, previousServiceId]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: String(item.id),
    data: { scriptIndex }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { field: serviceField } = useController({
    name: `scripts.${scriptIndex}.items.${index}.service`,
    control,
  });

  const selectedProduct = watch(`scripts.${scriptIndex}.items.${index}.productFinal`);
  const selectedProductId = selectedProduct?.id;

  const [availableScripts, setAvailableScripts] = useState<any[]>([]);
  const [isLoadingScripts, setIsLoadingScripts] = useState(false);

  useEffect(() => {
    const fetchAndSetScripts = async () => {
      if (selectedProductId) {
        setIsLoadingScripts(true);
        const scripts = await fetchScriptsByProductId(selectedProductId);
        setAvailableScripts(scripts);
        setIsLoadingScripts(false);

        const currentScript = watch(`scripts.${scriptIndex}.items.${index}.productScript`);

        if (scripts.length === 1) {
          setValue(`scripts.${scriptIndex}.items.${index}.productScript`, scripts[0]);
        }
        else if (scripts.length > 1) {
          if (currentScript) {
            const scriptExists = scripts.find(s => s.id === currentScript.id);
            if (!scriptExists) {
              setValue(`scripts.${scriptIndex}.items.${index}.productScript`, null);
            }
          }
        }
      } else {
        setAvailableScripts([]);
        setValue(`scripts.${scriptIndex}.items.${index}.productScript`, null);
      }
    };

    fetchAndSetScripts();
  }, [selectedProductId, scriptIndex, index, setValue, watch]);

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {isExpanded ? (
        <ExpandedItemContainer>
          <CollapsibleHeader>
            <ItemHeader {...listeners}>
              <DragHandle>
                <GripVertical size={16} color="#666" />
              </DragHandle>
              <ItemTitle>
                Item {index + 1} - {getItemTypeLabel(item.type)}
              </ItemTitle>
            </ItemHeader>
            <HeaderControls>
              {index > 0 && (
                <MoveButton
                  onClick={() => onMoveItem(scriptIndex, item.id, 'up')}
                >
                  ↑
                </MoveButton>
              )}
              {index < totalItems - 1 && (
                <MoveButton
                  onClick={() => onMoveItem(scriptIndex, item.id, 'down')}
                >
                  ↓
                </MoveButton>
              )}
              <CircularCollapseIcon onClick={() => onToggleExpansion(item.id)}>
                <ChevronDown
                  size={16}
                  style={{ transform: 'rotate(180deg)', transition: 'transform 0.2s ease' }}
                />
              </CircularCollapseIcon>
              <RemoveButton
                onClick={() => onRemove(scriptIndex, item.id)}
              >
                <Trash2 size={16} />
              </RemoveButton>
            </HeaderControls>
          </CollapsibleHeader>

          <FormGrid columns={item.type === 'resource' || item.type === 'productFinal' ? 3 : 2}>
            <FormGroup>
              <Label>Tipo de item</Label>
              <Select
                value={item.type}
                onChange={(e) => {
                  handleItemTypeChange(scriptIndex, index, e.target.value);
                }}
              >
                <option value="rawMaterial">Matéria Prima</option>
                <option value="service">Serviço</option>
                <option value="resource">Insumo</option>
                <option value="productFinal">Produto Manufaturado</option>
              </Select>
            </FormGroup>

            {item.type === 'rawMaterial' && (
              <>
                <FormGroup>
                  <Label>
                    Matéria Prima
                    <Tooltip text={errors.scripts?.[scriptIndex]?.items?.[index]?.rawMaterial?.message || ''}>
                      <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.items?.[index]?.rawMaterial ? "#F87171" : "#696969"} />
                    </Tooltip>
                  </Label>
                  <Controller
                    name={`scripts.${scriptIndex}.items.${index}.rawMaterial`}
                    control={control}
                    render={({ field }) => (
                      <InfiniteSelect
                        value={field.value}
                        onSelect={(option) => field.onChange(option)}
                        placeholder="Filtre por código ou nome"
                        searchPlaceholder="Filtre por código ou nome"
                        fetchFunction={fetchRawMaterials}
                        hasError={!!errors.scripts?.[scriptIndex]?.items?.[index]?.rawMaterial}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Peso</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="98,7kg"
                    {...control.register(`scripts.${scriptIndex}.items.${index}.weight`)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Medidas ideais</Label>
                  <Input
                    placeholder="Ex: 123x456x789mm"
                    {...control.register(`scripts.${scriptIndex}.items.${index}.idealMeasurements`)}
                  />
                </FormGroup>
              </>
            )}

            {item.type === 'resource' && (
              <>
                <FormGroup>
                  <Label>
                    Insumo
                    <Tooltip text={errors.scripts?.[scriptIndex]?.items?.[index]?.resource?.message || ''}>
                      <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.items?.[index]?.resource ? "#F87171" : "#696969"} />
                    </Tooltip>
                  </Label>
                  <ResourceServiceSelect
                    name={`scripts.${scriptIndex}.items.${index}.resource`}
                    control={control}
                    placeholder="Filtre por código ou nome"
                    searchPlaceholder="Filtre por código ou nome"
                    fetchFunction={fetchResources}
                    hasError={!!errors.scripts?.[scriptIndex]?.items?.[index]?.resource}
                    unitFieldName={`scripts.${scriptIndex}.items.${index}.unitOfMeasurementId`}
                    onUnitChange={(unitId: number) => updateItemUnitOfMeasurement?.(scriptIndex, index, unitId)}
                  />
                </FormGroup>

                {shouldShowResourceUnitSelect() ? (
                  <FlexRow>
                    <FlexFormGroup flex="1">
                      <Label>Unidade</Label>
                      <Select
                        value={unitField?.value || ''}
                        onChange={(e) => handleUnitSelect(Number(e.target.value))}
                      >
                        <option value="">Selecione a unidade...</option>
                        {getAvailableUnits().map((unit: any) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name} - R$ {parseFloat(unit.price).toFixed(2)}
                          </option>
                        ))}
                      </Select>
                    </FlexFormGroup>
                    <FlexFormGroup flex="1">
                      <Label>{getQuantityLabel()}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder={getQuantityPlaceholder()}
                        {...control.register(`scripts.${scriptIndex}.items.${index}.weight`)}
                      />
                    </FlexFormGroup>
                  </FlexRow>
                ) : (
                  <FormGroup>
                    <Label>{getQuantityLabel()}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder={getQuantityPlaceholder()}
                      {...control.register(`scripts.${scriptIndex}.items.${index}.weight`)}
                    />
                  </FormGroup>
                )}
              </>
            )}

            {item.type === 'productFinal' && (
              <>
                <FormGroup>
                  <Label>
                    Produto
                    <Tooltip text={errors.scripts?.[scriptIndex]?.items?.[index]?.productFinal?.message || ''}>
                      <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.items?.[index]?.productFinal ? "#F87171" : "#696969"} />
                    </Tooltip>
                  </Label>
                  <Controller
                    name={`scripts.${scriptIndex}.items.${index}.productFinal`}
                    control={control}
                    render={({ field }) => (
                      <InfiniteSelect
                        value={field.value}
                        onSelect={(option) => field.onChange(option)}
                        placeholder="Filtre por código ou nome"
                        searchPlaceholder="Filtre por código ou nome"
                        fetchFunction={fetchProducts}
                        hasError={!!errors.scripts?.[scriptIndex]?.items?.[index]?.productFinal}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>
                    Script
                    <Tooltip text={errors.scripts?.[scriptIndex]?.items?.[index]?.productScript?.message || ''}>
                      <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.items?.[index]?.productScript ? "#F87171" : "#696969"} />
                    </Tooltip>
                  </Label>
                  <div style={{ position: 'relative' }}>
                    <Controller
                      name={`scripts.${scriptIndex}.items.${index}.productScript`}
                      control={control}
                      render={({ field }) => (
                        <InfiniteSelect
                          value={field.value}
                          onSelect={(selectedScript) => {
                            field.onChange(selectedScript);
                            if (updateItemProductScript) {
                              updateItemProductScript(scriptIndex, index, selectedScript);
                            }
                          }}
                          placeholder={isLoadingScripts ? "Carregando..." : "Selecione um script"}
                          searchPlaceholder="Buscar script por ID ou nome..."
                          fetchFunction={async (limit: number, offset: number, search?: string) => {
                            if (!selectedProductId) return [];
                            const scripts = await fetchScriptsByProductId(selectedProductId, limit, offset, search);
                            return scripts;
                          }}
                          hasError={!!errors.scripts?.[scriptIndex]?.items?.[index]?.productScript}
                          disabled={!selectedProductId || isLoadingScripts}
                          initialOptions={(() => {
                            const currentScript = watch(`scripts.${scriptIndex}.items.${index}.productScript`);
                            return currentScript ? [currentScript] : [];
                          })()}
                        />
                      )}
                    />
                  </div>
                </FormGroup>
              </>
            )}

            {item.type === 'service' && (
              <>
                {shouldShowServiceUnitSelect() ? (
                  <FlexRow>
                    <FlexFormGroup flex="0.6">
                      <Label>
                        Serviço
                        <Tooltip text={errors.scripts?.[scriptIndex]?.items?.[index]?.service?.message || ''}>
                          <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.items?.[index]?.service ? "#F87171" : "#696969"} />
                        </Tooltip>
                      </Label>
                      <ResourceServiceSelect
                        name={`scripts.${scriptIndex}.items.${index}.service`}
                        control={control}
                        placeholder="Filtre por código ou nome"
                        searchPlaceholder="Filtre por código ou nome"
                        fetchFunction={fetchServices}
                        hasError={!!errors.scripts?.[scriptIndex]?.items?.[index]?.service}
                        unitFieldName={`scripts.${scriptIndex}.items.${index}.unitOfMeasurementId`}
                      />
                    </FlexFormGroup>
                    <FlexFormGroup flex="0.4">
                      <Label>Unidade</Label>
                      <Select
                        value={unitField?.value || ''}
                        onChange={(e) => handleUnitSelect(Number(e.target.value))}
                      >
                        <option value="">Selecione a unidade...</option>
                        {getAvailableServiceUnits().map((unit: any) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name} - R$ {parseFloat(unit.price).toFixed(2)}
                          </option>
                        ))}
                      </Select>
                    </FlexFormGroup>
                  </FlexRow>
                ) : (
                  <FormGroup>
                    <Label>
                      Serviço
                      <Tooltip text={errors.scripts?.[scriptIndex]?.items?.[index]?.service?.message || ''}>
                        <AlertCircle size={16} color={errors.scripts?.[scriptIndex]?.items?.[index]?.service ? "#F87171" : "#696969"} />
                      </Tooltip>
                    </Label>
                    <ResourceServiceSelect
                      name={`scripts.${scriptIndex}.items.${index}.service`}
                      control={control}
                      placeholder="Filtre por código ou nome"
                      searchPlaceholder="Filtre por código ou nome"
                      fetchFunction={fetchServices}
                      hasError={!!errors.scripts?.[scriptIndex]?.items?.[index]?.service}
                      unitFieldName={`scripts.${scriptIndex}.items.${index}.unitOfMeasurementId`}
                    />
                  </FormGroup>
                )}
              </>
            )}
          </FormGrid>

          {item.type === 'service' && (
            <>
              <FormGrid columns={4}>
                <FormGroup>
                  <Label>Apto para executar</Label>
                  <Controller
                    name={`scripts.${scriptIndex}.items.${index}.company`}
                    control={control}
                    render={({ field }) => (
                      <InfiniteSelect
                        value={field.value}
                        onSelect={(option) => field.onChange(option)}
                        placeholder="Selecione um fornecedor"
                        searchPlaceholder="Buscar fornecedor..."
                        fetchFunction={async (limit: number, offset: number, search?: string) => {
                          if (!selectedService?.id) return [];
                          const companies = await fetchCompaniesByStage(selectedService.id, limit, offset, search);
                          return companies.map((company: any) => ({
                            ...company,
                            name: company.name || company.companyName || 'Empresa sem nome'
                          }));
                        }}
                        hasError={false}
                        disabled={!selectedService?.id}
                        initialOptions={field.value ? [field.value] : []}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{getExecutionTimeLabel()}</Label>
                  <Input
                    type="time"
                    step="1"
                    {...control.register(`scripts.${scriptIndex}.items.${index}.executionTime`)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Qualificação necessária</Label>
                  <Input
                    placeholder="Ex: NR-12, ISO 9001"
                    {...control.register(`scripts.${scriptIndex}.items.${index}.requiredQualification`)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Prazo de entrega (dias)</Label>
                  <Input
                    type="number"
                    placeholder="60"
                    {...control.register(`scripts.${scriptIndex}.items.${index}.deliveryTime`)}
                  />
                </FormGroup>
              </FormGrid>

              <FormGrid columns={2}>
<FormGroup>
  <Label>Descrição</Label>
  <Controller
    name={`scripts.${scriptIndex}.items.${index}.description`}
    control={control}
    render={({ field }) => (
      <TextArea
        {...field}
        rows={3}
        placeholder="Digite a descrição"
      />
    )}
  />
</FormGroup>

<FormGroup>
  <Label>Observação</Label>
  <Controller
    name={`scripts.${scriptIndex}.items.${index}.observation`}
    control={control}
    render={({ field }) => (
      <TextArea
        {...field}
        rows={3}
        placeholder="Digite a observação"
      />
    )}
  />
</FormGroup>
              </FormGrid>
            </>
          )}
        </ExpandedItemContainer>
      ) : (
        <CollapsedItem>
          <CollapsedItemContent {...listeners}>
            <DragHandle>
              <GripVertical size={16} color="#666" />
            </DragHandle>
            <span>Item {index + 1} - {getItemTypeLabel(item.type)}</span>
          </CollapsedItemContent>
          <CollapsedItemControls>
            {index > 0 && (
              <MoveButton
                onClick={() => onMoveItem(scriptIndex, item.id, 'up')}
              >
                ↑
              </MoveButton>
            )}
            {index < totalItems - 1 && (
              <MoveButton
                onClick={() => onMoveItem(scriptIndex, item.id, 'down')}
              >
                ↓
              </MoveButton>
            )}
            <CircularCollapseIcon onClick={() => onToggleExpansion(item.id)}>
              <ChevronDown size={16} />
            </CircularCollapseIcon>
            <RemoveButton
              onClick={() => onRemove(scriptIndex, item.id)}
            >
              <Trash2 size={16} />
            </RemoveButton>
          </CollapsedItemControls>
        </CollapsedItem>
      )}
    </div>
  );
};

export const SortableItem = SortableItemComponent; 